// F3/FR-SH5a: pure helpers for the viewer-side gate on `/s/:token` (and `/g/:id?share=`,
// `/embed/:token`). Kept side-effect-free and separate from the components that render them so
// the branching logic (which factor is still outstanding, which error code means what) is
// unit-testable without a DOM — see `shareGateLogic.test.ts` and `clients/web/src/recorder/useRecorder.test.ts`
// for the same split elsewhere in this app.
import { ApiFail } from "../api/client";

export interface ShareGateInfo {
  password: boolean;
  email: boolean;
  domainRestricted: boolean;
  satisfiedPassword: boolean;
  satisfiedEmail: boolean;
}

interface ShareLockedData {
  gate?: { password?: boolean; email?: boolean; domain_restricted?: boolean };
  satisfied?: { password?: boolean; email?: boolean };
}

/** `null` unless `err` is the 401 `share_locked` shape `server/src/error.rs` renders —
 * `{"error":"share_locked","gate":{...},"satisfied":{...}}`, surfaced by `client.ts`'s
 * `toApiFail` as `ApiFail.data`. */
export function shareGateFromError(err: unknown): ShareGateInfo | null {
  if (!(err instanceof ApiFail) || err.code !== "share_locked") return null;
  const data = err.data as ShareLockedData | undefined;
  return {
    password: !!data?.gate?.password,
    email: !!data?.gate?.email,
    domainRestricted: !!data?.gate?.domain_restricted,
    satisfiedPassword: !!data?.satisfied?.password,
    satisfiedEmail: !!data?.satisfied?.email,
  };
}

/** 410: unknown, revoked, expired, or over `max_views` — byte-identical for all four causes
 * (server/src/error.rs's `ApiError::LinkUnavailable` doc comment); the UI must not try to
 * distinguish them either. */
export function isLinkUnavailable(err: unknown): boolean {
  return err instanceof ApiFail && err.code === "link_unavailable";
}

export type GateStep = "password" | "email" | "done";

/** Which factor the viewer still has to clear, given the gate shape and what's already
 * satisfied — password first (§2.7, no signed-in skip for passwords), then email. `"done"`
 * only happens transiently (the server itself would have returned 200, not `share_locked`,
 * once every required factor is satisfied) but is included so callers never match
 * exhaustively on just the two real steps. */
export function nextGateStep(gate: ShareGateInfo): GateStep {
  if (gate.password && !gate.satisfiedPassword) return "password";
  if (gate.email && !gate.satisfiedEmail) return "email";
  return "done";
}

/** `X-Grackle-Unlock` is checked ahead of `?unlock=` and the link-scoped cookie
 * (`link_protect::extract_unlock_token`) — the one header every share-scoped fetch in this
 * app should carry once a token (even a partial one) is held in memory. */
export function unlockHeaders(unlockToken: string | null): Record<string, string> | undefined {
  return unlockToken ? { "X-Grackle-Unlock": unlockToken } : undefined;
}

/** The 429/401 inline-error copy shared by the password and email-code forms (brief: "show
 * wrong-password (401) and rate-limited (429, 'Too many attempts, try again in a minute')
 * errors inline"). `wrongCode` is the gate-specific message for a non-rate-limit failure. */
export function gateErrorMessage(err: unknown, wrongCode: string, wrongMessage: string): string {
  if (err instanceof ApiFail) {
    if (err.status === 429) return "Too many attempts, try again in a minute.";
    if (err.code === wrongCode) return wrongMessage;
    if (err.code === "email_not_allowed") return "That email isn't allowed for this link.";
    return err.message;
  }
  return "Couldn't reach Grackle. Check your connection.";
}
